import { useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, Lock, User } from "lucide-react";

/**
 * Email/password sign-in & sign-up form. Used standalone on mobile (Capacitor)
 * where the Google web redirect is disabled, and as a secondary option on web.
 */
export default function EmailAuthForm({ onSuccess, compact = false }) {
  const { loginWithPassword, registerWithPassword } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitRef = useRef(null);

  // When the on-screen keyboard opens, the viewport shrinks; keep the submit
  // button in view so it isn't left hidden behind the keyboard.
  const revealSubmit = () => {
    setTimeout(() => submitRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 350);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    if (mode === "register" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    const fn = mode === "login" ? loginWithPassword : registerWithPassword;
    const res = mode === "login"
      ? await fn(email.trim(), password)
      : await fn(email.trim(), password, name.trim());
    setBusy(false);
    if (!res.success) {
      setError(res.error || "Authentication failed.");
      return;
    }
    onSuccess && onSuccess(res.user);
  };

  return (
    <form onSubmit={handleSubmit} onFocus={revealSubmit} className={`w-full max-w-sm mx-auto flex flex-col gap-3 ${compact ? "" : "p-1"}`} data-testid="email-auth-form">
      {mode === "register" && (
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500/40"
            data-testid="auth-name-input"
            autoComplete="name"
          />
        </div>
      )}
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500/40"
          data-testid="auth-email-input"
          autoComplete="email"
          inputMode="email"
          required
        />
      </div>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          type="password"
          placeholder={mode === "register" ? "Create a password (min 6)" : "Password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-500/40"
          data-testid="auth-password-input"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          required
        />
      </div>

      {error && (
        <p className="text-xs text-rose-400 text-center" data-testid="auth-error">{error}</p>
      )}

      <Button
        ref={submitRef}
        type="submit"
        disabled={busy}
        className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold rounded-full py-5 gap-2 shadow-[0_0_24px_rgba(245,158,11,0.18)]"
        data-testid={mode === "login" ? "email-login-submit" : "email-register-submit"}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {mode === "login" ? "Sign in" : "Create account"}
      </Button>

      <button
        type="button"
        onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
        className="text-xs text-slate-400 hover:text-amber-400 transition-colors"
        data-testid="toggle-auth-mode"
      >
        {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
      </button>
    </form>
  );
}
